import * as Sentry from '@sentry/react';
import { produce } from 'immer';
import store, { State } from 'store';
import postAsync from './postAsync';

import ISystemFile from 'types/ISystemFile';

export default function uploadFiles() {
  const res = store.getState().startWorkflow.submit.uploadUrlsAsync;
  const provideFiles = store.getState().startWorkflow.steps.provideFiles;

  if (res.status !== 'SUCCESS' && res.status !== 'REFRESH') {
    return Promise.resolve();
  }

  const tags = (res.data as Record<string, unknown>).tags as Array<{
    tag_name: string;
    links: Array<{
      filename: string;
      url: string;
    }>;
  }>;

  const links = tags.flatMap((tag) => tag.links);

  const promises: Promise<unknown>[] = Object.entries(provideFiles.fileMap)
    .filter(([_key, files]) => !!files)
    .flatMap(([key, files]) =>
      (files as ISystemFile[]).map(
        (file, index) => [key, { file, index: index }] as [string, { file: ISystemFile; index: number }],
      ),
    )
    .map(([key, { file, index }]) => {
      return postAsync<void>({
        post() {
          const link = links.find((link) => link.filename.endsWith(`${key}/${index}/${file.name}`));

          if (!link) {
            const error = Error(`No upload url for file /${key}/${index}/${file.name}`);
            Sentry.captureException(error);

            // NB: Reject inside postAsync to make the files async status "ERROR",
            // will then be shown in UI
            return Promise.reject(error);
          }

          // Unfortunately we need to conver the file to array buffer. That is
          // done via a listener on a FileReader, so we need to wrap this in a
          // promise and resolve once (1) file is read and (2) we have uploaded.
          return new Promise<void>((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.onload = (event: ProgressEvent<FileReader>) => {
              const buffer = event.target?.result;

              // The response from this request is not valid json. Thus,
              // response.json() will throw with "Unexpected end of JSON input".
              // Then we might as well use plain fetch instead of ky.
              return fetch(link.url, {
                method: 'PUT',
                body: buffer,
              })
                .then((response) => {
                  if (!response.ok) {
                    throw new Error(`${response.status}: ${response.statusText}`);
                  }
                  return response;
                })
                .then(
                  () => resolve(),
                  (error) => {
                    Sentry.captureException(error);
                    reject(error);
                  },
                );
            };
            fileReader.onerror = reject;
            fileReader.onabort = reject;

            // @ts-expect-error – The file is a valid Blob
            fileReader.readAsArrayBuffer(file);
          });
        },
        getAsync() {
          return (
            store.getState().startWorkflow.submit.uploadAsyncMap[`${key}/${index}/${file.name}`] || { status: 'INIT' }
          );
        },
        setAsync(async) {
          store.setState(
            produce((state: State) => {
              state.startWorkflow.submit.uploadAsyncMap[`${key}/${index}/${file.name}`] = async;
            }),
          );
        },
      });
    });

  return Promise.all(promises);
}
